import React, { useState, useEffect } from 'react';
import ApiService from '../../services/apiService';
import './LibrarySliderManager.css';

const LibrarySliderManager = () => {
  const [sliderImages, setSliderImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    alt_text: '',
    display_order: '',
    image: null
  });

  useEffect(() => {
    loadSliderImages();
  }, []);

  const loadSliderImages = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllLibrarySliderImages();
      if (response.success) {
        setSliderImages(response.data);
      } else {
        setError('Failed to load library slider images');
      }
    } catch (error) {
      console.error('Error loading library slider images:', error);
      setError('Error loading library slider images');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      let response;
      response = await ApiService.createLibrarySliderImage(Object.fromEntries(submitData));

      if (response.success) {
        await loadSliderImages();
        resetForm();
        setError('');
      } else {
        setError(response.message || 'Failed to save library slider image');
      }
    } catch (error) {
      console.error('Error saving library slider image:', error);
      setError('Error saving library slider image');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this library slider image?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.deleteLibrarySliderImage(id);
      if (response.success) {
        await loadSliderImages();
        setError('');
      } else {
        setError('Failed to delete library slider image');
      }
    } catch (error) {
      console.error('Error deleting library slider image:', error);
      setError('Error deleting library slider image');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await ApiService.toggleLibrarySliderImageStatus(id);
      if (response.success) {
        await loadSliderImages();
        setError('');
      } else {
        setError('Failed to toggle library slider image status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      setError('Error toggling status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      alt_text: '',
      display_order: '',
      image: null
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const moveImage = async (id, direction) => {
    const currentIndex = sliderImages.findIndex(img => img.id === id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= sliderImages.length) return;

    try {
      const currentImage = sliderImages[currentIndex];
      const targetImage = sliderImages[targetIndex];
      
      await ApiService.updateLibrarySliderImageOrder(currentImage.id, targetImage.display_order);
      await ApiService.updateLibrarySliderImageOrder(targetImage.id, currentImage.display_order);
      
      await loadSliderImages();
    } catch (error) {
      console.error('Error moving image:', error);
      setError('Error reordering images');
    }
  };

  if (loading && sliderImages.length === 0) {
    return <div className="library-slider-loading">Loading library slider images...</div>;
  }

  return (
    <div className="library-slider-manager">
      <div className="library-slider-manager-header">
        <h2>Library Slider Management</h2>
        <button 
          className="library-slider-add-btn" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Add New Image'}
        </button>
      </div>

      {error && <div className="library-slider-error-message">{error}</div>}

      {showForm && (
        <div className="library-slider-form-container">
          <h3>Add New Library Slider Image</h3>
          <form onSubmit={handleSubmit} className="library-slider-form">
            <div className="library-slider-form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter image title"
              />
            </div>

            <div className="library-slider-form-group">
              <label htmlFor="subtitle">Subtitle</label>
              <input
                type="text"
                id="subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleInputChange}
                placeholder="Enter image subtitle"
              />
            </div>

            <div className="library-slider-form-group">
              <label htmlFor="alt_text">Alt Text</label>
              <input
                type="text"
                id="alt_text"
                name="alt_text"
                value={formData.alt_text}
                onChange={handleInputChange}
                placeholder="Enter alt text for accessibility"
              />
            </div>

            <div className="library-slider-form-group">
              <label htmlFor="display_order">Display Order</label>
              <input
                type="number"
                id="display_order"
                name="display_order"
                value={formData.display_order}
                onChange={handleInputChange}
                min="0"
                placeholder="Display order (leave empty for auto)"
              />
            </div>

            <div className="library-slider-form-group">
              <label htmlFor="image">Image *</label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleInputChange}
                accept="image/*"
                required
              />
            </div>

            <div className="library-slider-form-actions">
              <button 
                type="submit" 
                className="library-slider-btn library-slider-btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Add Image'}
              </button>
              <button 
                type="button" 
                className="library-slider-btn library-slider-btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="library-slider-images-list">
        <h3>Current Library Slider Images ({sliderImages.length})</h3>
        {sliderImages.length === 0 ? (
          <p className="library-slider-no-images">No library slider images found. Add some images to get started!</p>
        ) : (
          <div className="library-slider-images-grid">
            {sliderImages.map((image, index) => (
              <div key={image.id} className={`library-slider-image-card ${!image.is_active ? 'inactive' : ''}`}>
                <div className="library-slider-image-preview">
                  {image.image_url ? (
                    <img src={image.image_url} alt={image.alt_text || image.title} />
                  ) : (
                    <div className="library-slider-no-image">No Image</div>
                  )}
                </div>
                
                <div className="library-slider-image-info">
                  <h4>{image.title}</h4>
                  {image.subtitle && <p className="library-slider-subtitle">{image.subtitle}</p>}
                  <div className="library-slider-image-meta">
                    <span className={`library-slider-status ${image.is_active ? 'active' : 'inactive'}`}>
                      {image.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="library-slider-order">Order: {image.display_order}</span>
                  </div>
                </div>

                <div className="library-slider-image-actions">
                  <button 
                    className="library-slider-btn library-slider-btn-danger library-slider-btn-small"
                    onClick={() => handleDelete(image.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LibrarySliderManager;